import {Injectable} from '@angular/core';
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {MetricsService} from '../../services/metrics.service';
import {Observable, switchMap, withLatestFrom} from 'rxjs';
import {Serializer, SerializerResponse, Serializers} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {selectSite} from '../init/init.selectors';
import {TypedAction} from '@ngrx/store/src/models';
import {DataSetMetricsGroup} from '../../interfaces/metrics';
import {
  createDataSetMetricsGroup,
  createDataSetMetricsGroupSuccess, deleteDataSetMetricsGroup, deleteDataSetMetricsGroupSuccess,
  initializeDataSetMetricsGroups,
  initializeDataSetMetricsGroupsSuccess,
  loadDataSetMetricsGroups,
  loadDataSetMetricsGroupsSuccess,
  updateDataSetMetricsGroup, updateDataSetMetricsGroups, updateDataSetMetricsGroupsSuccess,
  updateDataSetMetricsGroupSuccess
} from './dataSetMetricsGroups.actions';
import {filter, map} from 'rxjs/operators';
import {
  createDataSetMetric,
  createDataSetMetricSuccess,
  deleteDataSetMetric,
  deleteDataSetMetricSuccess,
  loadDataSetMetrics,
  loadDataSetMetricsSuccess,
  updateDataSetMetric, updateDataSetMetrics, updateDataSetMetricsSuccess,
  updateDataSetMetricSuccess
} from '../dataSetMetrics/dataSetMetrics.actions';
import {MetricComponentObjects} from '../../classes/metric-component-object';
import {selectDataSetMetricsGroupsState} from './dataSetMetricsGroups.selectors';
import {selectDataSetMetricsState} from '../dataSetMetrics/dataSetMetrics.selectors';
import {DataSetMetricsState} from '../dataSetMetrics/dataSetMetrics';
import {DataSetMetricsGroupsState} from './dataSetMetricsGroups';

@Injectable()
export class DataSetMetricsGroupsEffects {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);

  private readonly _load$: Observable<TypedAction<string> & { groups: Serializers<DataSetMetricsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { groups: Serializers<DataSetMetricsGroup> }> => this._actions$.pipe(
    ofType(loadDataSetMetricsGroups),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetMetricsGroup>>> => this._metricsService.getDataSetMetricsGroups(dataset)),
    map((response: SerializerResponse<Serializers<DataSetMetricsGroup>>): TypedAction<string> & { groups: Serializers<DataSetMetricsGroup> } => loadDataSetMetricsGroupsSuccess({
      groups: response.data
    }))
  ));

  private readonly _updates$: Observable<TypedAction<string> & { groups: Serializers<DataSetMetricsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { groups: Serializers<DataSetMetricsGroup> }> => this._actions$.pipe(
    ofType(updateDataSetMetricsGroups),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetMetricsGroup>>> => this._metricsService.getDataSetMetricsGroups(dataset)),
    map((response: SerializerResponse<Serializers<DataSetMetricsGroup>>): TypedAction<string> & { groups: Serializers<DataSetMetricsGroup> } => updateDataSetMetricsGroupsSuccess({
      groups: response.data
    }))
  ));

  private readonly _create$: Observable<TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }> => this._actions$.pipe(
    ofType(createDataSetMetricsGroup),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { group: Partial<DataSetMetricsGroup> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetMetricsGroup>>> => this._metricsService.createDataSetMetricsGroup(dataset, action.group)),
    map((response: SerializerResponse<Serializer<DataSetMetricsGroup>>): TypedAction<string> & { group: Serializer<DataSetMetricsGroup> } => createDataSetMetricsGroupSuccess({
      group: response.data
    }))
  ));

  private readonly _update$: Observable<TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }> => this._actions$.pipe(
    ofType(updateDataSetMetricsGroup),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { group: Serializer<DataSetMetricsGroup>, update: Partial<DataSetMetricsGroup> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetMetricsGroup>>> => this._metricsService.updateDataSetMetricsGroup(dataset, action.group, action.update)),
    map((response: SerializerResponse<Serializer<DataSetMetricsGroup>>): TypedAction<string> & { group: Serializer<DataSetMetricsGroup> } => updateDataSetMetricsGroupSuccess({
      group: response.data
    }))
  ));

  private readonly _delete$: Observable<TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }> => this._actions$.pipe(
    ofType(deleteDataSetMetricsGroup),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { group: Serializer<DataSetMetricsGroup> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetMetricsGroup>>> => this._metricsService.deleteDataSetMetricsGroup(dataset, action.group)),
    map((response: SerializerResponse<Serializer<DataSetMetricsGroup>>): TypedAction<string> & { group: Serializer<DataSetMetricsGroup> } => deleteDataSetMetricsGroupSuccess({
      group: response.data
    }))
  ));

  private readonly _init$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(
      loadDataSetMetrics,
      updateDataSetMetrics,
      createDataSetMetric,
      updateDataSetMetric,
      deleteDataSetMetric,
      loadDataSetMetricsGroups,
      updateDataSetMetricsGroups,
      createDataSetMetricsGroup,
      updateDataSetMetricsGroup,
    ),
    map((): TypedAction<string> => initializeDataSetMetricsGroups())
  ));

  private readonly _initSuccess$: Observable<TypedAction<string> & { datasetMetrics: MetricComponentObjects }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { datasetMetrics: MetricComponentObjects }> => this._actions$.pipe(
    ofType(
      loadDataSetMetricsSuccess,
      updateDataSetMetricsSuccess,
      createDataSetMetricSuccess,
      updateDataSetMetricSuccess,
      deleteDataSetMetricSuccess,
      loadDataSetMetricsGroupsSuccess,
      updateDataSetMetricsGroupsSuccess,
      createDataSetMetricsGroupSuccess,
      updateDataSetMetricsGroupSuccess,
    ),
    withLatestFrom(
      this._store.select(selectDataSetMetricsState),
      this._store.select(selectDataSetMetricsGroupsState)
    ),
    filter(([_, datasetMetricsState, datasetMetricsGroupsState]: [TypedAction<string>, DataSetMetricsState, DataSetMetricsGroupsState]): boolean => datasetMetricsState.loaded && datasetMetricsGroupsState.loaded),
    map(([_, datasetMetricsState]: [TypedAction<string>, DataSetMetricsState, DataSetMetricsGroupsState]): TypedAction<string> & { datasetMetrics: MetricComponentObjects } => initializeDataSetMetricsGroupsSuccess({
      datasetMetrics: datasetMetricsState.componentObjects.all
    }))
  ));

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<AppState>,
    private readonly _metricsService: MetricsService
  ) {}

}
